import { useMemo, useRef, useState } from 'react';
import SafeIcon from './SafeIcon';
import MaterialPicker from './MaterialPicker';
import DriveBrowser from './DriveBrowser';
import { useApp } from '../i18n/AppContext';
import { useUi } from '../store/ui';
import { useDemo, useWorkspaceData } from '../store/DemoStore';
import type { Asset, Upload } from '../store/seed';

type Badge = 'Linked' | 'Used in posts' | 'Preparing media';
const badgeStyle: Record<Badge, { statusClass: string; icon: string }> = {
  Linked: { statusClass: 'neutral', icon: 'FiLink' },
  'Used in posts': { statusClass: 'scheduled', icon: 'FiCheckCircle' },
  'Preparing media': { statusClass: 'ready', icon: 'FiLoader' },
};
const kindIcon: Record<string, string> = {
  Photo: 'FiImage',
  Video: 'FiFilm',
  Audio: 'FiMusic',
  Document: 'FiFileText',
};
const filterKinds: Record<string, string | undefined> = {
  'All files': undefined,
  Photos: 'Photo',
  Videos: 'Video',
  Audio: 'Audio',
  Documents: 'Document',
};
const kindFromFile = (file: File): Asset['kind'] =>
  file.type.startsWith('image') || /\.(heic|heif|dng)$/i.test(file.name)
    ? 'Photo'
    : file.type.startsWith('video') || /\.(mov|mp4|m4v)$/i.test(file.name)
      ? 'Video'
      : file.type.startsWith('audio')
        ? 'Audio'
        : 'Document';

export default function MaterialBank() {
  const { translate, readOnlyProps, readOnly, language } = useApp();
  const { route, notify, actor, navigate } = useUi();
  const { commands } = useDemo();
  const { workspace, assets, posts, uploads, drive } = useWorkspaceData(route.workspaceId);
  const missing = posts.filter((post) => post.status === 'Needs your input');
  const [filter, setFilter] = useState('All files');
  const [tag, setTag] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [openedAssetId, setOpenedAssetId] = useState<string | null>(null);
  const [driveOpen, setDriveOpen] = useState<'connect' | 'browse' | null>(null);
  const [resolve, setResolve] = useState<{ postId: string; method: 'clips' | 'photos' } | null>(
    null
  );
  const inputRef = useRef<HTMLInputElement>(null);
  const withoutQueryString = (imageUrl: string) => imageUrl.split('?')[0];
  const postImageMatchesAsset = (postImage: string, asset: Asset) => {
    const addedInThisSessionNeedsExactFile = asset.added;
    return addedInThisSessionNeedsExactFile
      ? postImage === asset.image
      : withoutQueryString(postImage) === withoutQueryString(asset.image);
  };
  const postsUsingAsset = (asset: Asset) =>
    posts.filter((post) => post.image && postImageMatchesAsset(post.image, asset));
  const tags = useMemo(
    () => [...new Set(assets.flatMap((asset) => asset.tags ?? []))].sort(),
    [assets]
  );
  const visible = assets.filter(
    (asset) =>
      (showArchived ? asset.archived : !asset.archived) &&
      (!filterKinds[filter] || asset.kind === filterKinds[filter]) &&
      (!tag || asset.tags?.includes(tag)) &&
      (asset.name.toLowerCase().includes(query.toLowerCase()) ||
        (asset.tags ?? []).some((assetTag) => assetTag.includes(query.toLowerCase())))
  );
  const sorted = [...visible].sort(
    (first, second) => Number(!!second.favourite) - Number(!!first.favourite)
  );
  const active = uploads.filter(
    (upload) => upload.state !== 'done' && upload.state !== 'cancelled'
  );
  const finished = uploads.filter(
    (upload) => upload.state === 'done' || upload.state === 'cancelled'
  );

  function onFiles(list: FileList | null) {
    if (!list?.length) return;
    const files = [...list].map((file) => ({
      name: file.name,
      kind: kindFromFile(file),
      sizeMb: Math.max(1, Math.round(file.size / 1024 / 1024)),
      src:
        file.type.startsWith('image') && file.size < 900 * 1024
          ? URL.createObjectURL(file)
          : undefined,
    }));
    commands.addUploads(workspace.id, files);
    notify(translate('{n} files added to the upload queue', { n: files.length }));
  }

  function resolveWith(items: { name: string }[]) {
    if (!resolve) return;
    commands.resolveMissing(
      resolve.postId,
      resolve.method,
      items.map((item) => item.name),
      actor
    );
    setResolve(null);
    notify(
      translate(
        resolve.method === 'clips'
          ? 'Clips added. AMY is preparing the post and will ask you to review it.'
          : 'Photos chosen. AMY is preparing a carousel and will ask you to review it.'
      )
    );
  }

  const openedAsset = assets.find((item) => item.id === openedAssetId);

  return (
    <div className="content-stack materials-page">
      <div className="page-title-row">
        <div>
          <h1>{translate('Material bank')}</h1>
          <div className="subtitle">
            {translate('Your creative library for {ws}', { ws: workspace.name })}
          </div>
        </div>
        <input
          ref={inputRef}
          type="file"
          multiple
          hidden
          accept="image/*,video/*,audio/*,.pdf,.heic,.heif,.dng,.mov"
          onChange={(event) => {
            onFiles(event.target.files);
            event.target.value = '';
          }}
        />
        <button
          className="button primary"
          onClick={() => inputRef.current?.click()}
          {...readOnlyProps}
        >
          <SafeIcon name="FiUpload" />
          {translate('Add photos, videos or audio')}
        </button>
      </div>

      {drive.connected ? (
        <div className={`notice-banner drive-card ${drive.access === 'lost' ? 'warn' : ''}`}>
          <SafeIcon name="FiHardDrive" />
          <div style={{ flex: 1, minWidth: 220 }}>
            <strong>Google Drive – {drive.folder}</strong>
            <div className="muted">
              {drive.access === 'lost'
                ? translate(
                    'AMY can no longer open this folder. Ask the folder owner to share it again, or choose another folder.'
                  )
                : translate('Linked folder · {n} files · checked at {time}', {
                    n: drive.fileCount,
                    time: drive.lastChecked,
                  })}
            </div>
            <div className="muted small-print">
              {translate(
                "Your photos and videos stay in your Google Drive. AMY only keeps copies of what's used in your posts."
              )}{' '}
              · {drive.account}
            </div>
          </div>
          <span className={`status ${drive.access === 'lost' ? 'input' : 'scheduled'}`}>
            <SafeIcon name={drive.access === 'lost' ? 'FiAlertTriangle' : 'FiCheckCircle'} />
            {translate(drive.access === 'lost' ? 'No access' : 'Connected')}
          </span>
          <div className="button-row">
            <button
              className="button small"
              onClick={() => setDriveOpen('browse')}
              disabled={drive.access === 'lost'}
            >
              <SafeIcon name="FiFolder" size={14} />
              {translate('Browse')}
            </button>
            <button
              className="button small"
              onClick={() => {
                commands.refreshDrive(workspace.id);
                notify(
                  translate('Folder checked. {n} files, nothing new.', { n: drive.fileCount })
                );
              }}
            >
              <SafeIcon name="FiRefreshCw" size={14} />
              {translate('Refresh')}
            </button>
            {drive.access === 'lost' ? (
              <button
                className="button small primary"
                onClick={() => {
                  commands.setDriveAccess(workspace.id, 'ok');
                  notify(translate('Access restored (simulated)'));
                }}
                {...readOnlyProps}
              >
                {translate('Fix access')}
              </button>
            ) : (
              <button
                className="button small ghost"
                onClick={() => commands.setDriveAccess(workspace.id, 'lost')}
                title={translate('Demo only')}
              >
                {translate('Simulate lost access')}
              </button>
            )}
            <button
              className="button small ghost"
              onClick={() => setDriveOpen('connect')}
              {...readOnlyProps}
            >
              {translate('Change folder')}
            </button>
          </div>
        </div>
      ) : (
        <div className="notice-banner drive-card">
          <SafeIcon name="FiHardDrive" />
          <div style={{ flex: 1 }}>
            <strong>{translate('Use your Google Drive as a library')}</strong>
            <div className="muted">
              {translate(
                'Link one folder. AMY lists the files and only copies the ones you use in posts. Nothing is imported in bulk.'
              )}
            </div>
          </div>
          <button
            className="button primary small"
            onClick={() => setDriveOpen('connect')}
            {...readOnlyProps}
          >
            <SafeIcon name="FiLink" size={14} />
            {translate('Connect a folder')}
          </button>
        </div>
      )}

      {missing.length > 0 && (
        <div className="panel missing-panel">
          <h2>
            <SafeIcon name="FiHelpCircle" size={18} /> {translate('AMY needs material')}
          </h2>
          {missing.map((post) => (
            <div key={post.id} className="missing-item">
              <p>
                <strong>{post.title}</strong> · {post.channel} ·{' '}
                <button
                  className="link-button"
                  onClick={() => navigate({ page: 'post', id: post.id })}
                >
                  {translate('Open post')}
                </button>
              </p>
              <p className="muted">{translate(post.missing ?? '')}</p>
              <div className="button-row">
                <button
                  className="button primary"
                  onClick={() => setResolve({ postId: post.id, method: 'clips' })}
                  {...readOnlyProps}
                >
                  <SafeIcon name="FiUpload" />
                  {translate('Add clips')}
                </button>
                <button
                  className="button"
                  onClick={() => setResolve({ postId: post.id, method: 'photos' })}
                  {...readOnlyProps}
                >
                  <SafeIcon name="FiImage" />
                  {translate('Use photos instead')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="filter-row materials-filters">
        <input
          className="search-field"
          style={{ maxWidth: 230 }}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={translate('Search names and tags')}
          aria-label={translate('Search materials')}
        />
        {Object.keys(filterKinds).map((item) => (
          <button
            className={`channel-chip ${filter === item ? 'selected' : ''}`}
            aria-pressed={filter === item}
            key={item}
            onClick={() => setFilter(item)}
          >
            {translate(item)}
          </button>
        ))}
        <label className="brand-check compact">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(event) => setShowArchived(event.target.checked)}
          />{' '}
          {translate('Archived only')}
        </label>
      </div>
      {tags.length > 0 && (
        <div className="tag-row" aria-label={translate('Tags')}>
          {tags.map((item) => (
            <button
              key={item}
              className={`tag-chip ${tag === item ? 'selected' : ''}`}
              aria-pressed={tag === item}
              onClick={() => setTag(tag === item ? null : item)}
            >
              #{item}
            </button>
          ))}
        </div>
      )}
      {sorted.length === 0 && (
        <div className="panel calendar-empty">
          <SafeIcon name="FiImage" />
          <p>{translate(showArchived ? 'No archived files.' : 'No files match these filters.')}</p>
        </div>
      )}
      <div className="grid-cards">
        {sorted.map((item) => {
          const used = postsUsingAsset(item);
          const badges = [
            ...new Set([
              ...item.badges.filter((existingBadge) => existingBadge !== 'Used in posts'),
              ...(used.length ? ['Used in posts' as const] : []),
            ]),
          ] as Badge[];
          return (
            <button
              className="card material-card list-button"
              key={item.id}
              onClick={() => setOpenedAssetId(item.id)}
              aria-label={translate('Open {name}', { name: item.name })}
            >
              <div className="material-thumb-wrap">
                {item.image ? (
                  <img className="material-thumb" src={item.image} alt="" />
                ) : (
                  <span className="material-thumb placeholder">
                    <SafeIcon name={kindIcon[item.kind] as never} size={30} />
                  </span>
                )}
                {item.kind !== 'Photo' && item.dur && (
                  <span className="status neutral thumb-badge">
                    <SafeIcon name={item.kind === 'Video' ? 'FiPlay' : 'FiMusic'} size={11} />
                    {item.dur}
                  </span>
                )}
                {item.favourite && (
                  <span className="fav-star" aria-label={translate('Favourite')}>
                    <SafeIcon name="FiStar" size={14} />
                  </span>
                )}
              </div>
              <p>
                <strong>{item.name}</strong>
              </p>
              <span className="muted">
                {translate(item.kind)}
                {item.dur ? ` · ${item.dur}` : ''}
                {used.length ? ` · ${translate('Used in {x}', { x: used.length })}` : ''}
              </span>
              {badges.length > 0 && (
                <div className="material-badges">
                  {badges.map((badge) => (
                    <span className={`status ${badgeStyle[badge].statusClass}`} key={badge}>
                      <SafeIcon name={badgeStyle[badge].icon as 'FiLink'} size={11} />
                      {translate(
                        badge === 'Preparing media' && item.preparingUntil
                          ? 'Preparing media'
                          : badge
                      )}
                    </span>
                  ))}
                </div>
              )}
            </button>
          );
        })}
      </div>

      <div className="panel">
        <div className="panel-header">
          <h2>{translate('Upload queue')}</h2>
          <span className="muted small-print">
            {translate(
              'Uploading and preparing are separate steps. You can close the app while files prepare.'
            )}
          </span>
        </div>
        {active.length === 0 && (
          <p className="muted">{translate('Nothing uploading right now.')}</p>
        )}
        {active.map((upload) => (
          <UploadRow
            key={upload.id}
            upload={upload}
            readOnly={readOnly}
            onAction={(action) => {
              if (action === 'interrupt') commands.interruptUpload(upload.id);
              else commands.setUploadState(upload.id, action);
            }}
          />
        ))}
        {finished.length > 0 && (
          <details className="post-activity">
            <summary>{translate('Finished ({n})', { n: finished.length })}</summary>
            {finished.map((upload) => (
              <div key={upload.id} className="activity-row">
                <span className="muted">
                  {translate(upload.state === 'done' ? 'Ready' : 'Cancelled')}
                </span>
                <span>{upload.name}</span>
              </div>
            ))}
          </details>
        )}
      </div>

      <div className="panel">
        <strong>{translate('Before uploading')}</strong>
        <p className="muted">{translate('Video up to 10 GB · Image 200 MB · Audio 2 GB')}</p>
        <p className="muted">
          {translate(
            'We keep an optimised copy for your posts. Keep your originals on your phone or drive.'
          )}
        </p>
      </div>

      {openedAsset && (
        <AssetDetail
          asset={openedAsset}
          used={postsUsingAsset(openedAsset)}
          language={language}
          onClose={() => setOpenedAssetId(null)}
        />
      )}
      {driveOpen && <DriveBrowser mode={driveOpen} onClose={() => setDriveOpen(null)} />}
      {resolve && (
        <MaterialPicker
          title={
            resolve.method === 'clips' ? 'Add clips for this post' : 'Choose photos for this post'
          }
          multiple
          kinds={resolve.method === 'clips' ? ['Video'] : ['Photo']}
          onCancel={() => setResolve(null)}
          onPick={resolveWith}
        />
      )}
    </div>
  );
}

function UploadRow({
  upload,
  readOnly,
  onAction,
}: {
  upload: Upload;
  readOnly: boolean;
  onAction: (
    action: 'pause' | 'resume' | 'retry' | 'cancel' | 'dismiss' | 'keepBoth' | 'interrupt'
  ) => void;
}) {
  const { translate } = useApp();
  const size =
    upload.sizeMb >= 1000 ? `${(upload.sizeMb / 1000).toFixed(1)} GB` : `${upload.sizeMb} MB`;
  const stateLabel: Record<string, string> = {
    uploading: 'Uploading',
    paused: 'Paused',
    preparing: 'Preparing for posts',
    failed: 'Upload stopped',
    unsupported: 'Cannot be used',
    duplicate: 'Already in the library',
  };
  const statusClass =
    upload.state === 'failed' || upload.state === 'unsupported'
      ? 'input'
      : upload.state === 'duplicate' || upload.state === 'paused'
        ? 'review'
        : upload.state === 'preparing'
          ? 'ready'
          : 'scheduled';
  return (
    <div className="checklist-item upload-row">
      <SafeIcon name={kindIcon[upload.kind] as never} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <strong className="upload-name">
          {upload.name} · {size}
        </strong>
        {(upload.state === 'uploading' ||
          upload.state === 'paused' ||
          upload.state === 'failed' ||
          upload.state === 'preparing') && (
          <div className="progress-track">
            <span
              className={upload.state === 'preparing' ? 'progress-indeterminate' : ''}
              style={upload.state === 'preparing' ? undefined : { width: `${upload.progress}%` }}
            />
          </div>
        )}
        <span className="muted">
          <span className={`status ${statusClass}`}>
            {translate(stateLabel[upload.state] ?? upload.state)}
          </span>{' '}
          {upload.state === 'uploading' || upload.state === 'paused' || upload.state === 'failed'
            ? `${upload.progress}%`
            : ''}{' '}
          {upload.note
            ? translate(upload.note, upload.noteVars)
            : upload.state === 'uploading'
              ? translate('You can keep using the app.')
              : ''}
        </span>
      </div>
      <div className="button-row upload-actions">
        {upload.state === 'uploading' && (
          <>
            <button
              className="icon-button"
              onClick={() => onAction('pause')}
              aria-label={translate('Pause upload')}
              disabled={readOnly}
            >
              <SafeIcon name="FiPause" />
            </button>
            <button
              className="button small ghost"
              onClick={() => onAction('interrupt')}
              title={translate('Demo only')}
            >
              {translate('Simulate drop')}
            </button>
          </>
        )}
        {upload.state === 'paused' && (
          <button
            className="icon-button"
            onClick={() => onAction('resume')}
            aria-label={translate('Resume upload')}
            disabled={readOnly}
          >
            <SafeIcon name="FiPlay" />
          </button>
        )}
        {upload.state === 'failed' && (
          <button
            className="button small primary"
            onClick={() => onAction('retry')}
            disabled={readOnly}
          >
            <SafeIcon name="FiRotateCw" size={14} />
            {translate('Retry')}
          </button>
        )}
        {upload.state === 'duplicate' && (
          <button className="button small" onClick={() => onAction('keepBoth')} disabled={readOnly}>
            {translate('Keep both')}
          </button>
        )}
        {(upload.state === 'unsupported' || upload.state === 'duplicate') && (
          <button className="button small ghost" onClick={() => onAction('dismiss')}>
            {translate('Dismiss')}
          </button>
        )}
        {['uploading', 'paused', 'failed'].includes(upload.state) && (
          <button
            className="icon-button"
            onClick={() => onAction('cancel')}
            aria-label={translate('Cancel upload')}
            disabled={readOnly}
          >
            <SafeIcon name="FiX" />
          </button>
        )}
      </div>
    </div>
  );
}

function AssetDetail({
  asset,
  used,
  language: language,
  onClose,
}: {
  asset: Asset;
  used: { id: string; title: string; channel: string; day: number }[];
  language: string;
  onClose: () => void;
}) {
  const { translate, readOnlyProps, readOnly } = useApp();
  const { route, navigate, notify } = useUi();
  const { commands } = useDemo();
  const [name, setName] = useState(asset.name);
  const [newTag, setNewTag] = useState('');
  const [zoom, setZoom] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const save = (patch: Partial<Asset>) => !readOnly && commands.updateAsset(asset.id, patch);
  const suitable =
    asset.kind === 'Video'
      ? ['Reels', 'Stories', 'TikTok']
      : asset.kind === 'Photo'
        ? ['Feed posts', 'Carousels', 'Stories']
        : asset.kind === 'Audio'
          ? ['Reels background', 'Stories']
          : ['LinkedIn documents'];
  const formatNovemberDay = (dayOfMonth: number) =>
    new Intl.DateTimeFormat(language, { day: 'numeric', month: 'short' }).format(
      new Date(2026, 10, dayOfMonth)
    );

  return (
    <div
      className="drawer-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className="drawer asset-detail"
        role="dialog"
        aria-modal="true"
        aria-labelledby="asset-detail-title"
      >
        <div className="drawer-header">
          <div>
            <h2 id="asset-detail-title">{asset.name}</h2>
            <div className="muted">
              {asset.file ?? asset.name} {asset.size ? `· ${asset.size}` : ''}
            </div>
          </div>
          <button className="icon-button" onClick={onClose} aria-label={translate('Close')}>
            <SafeIcon name="FiX" />
          </button>
        </div>
        <div className={`asset-detail-media ${zoom ? 'zoomed' : ''}`}>
          {asset.kind === 'Video' ? (
            <video
              src="https://interactive-examples.mdn.mozilla.net/media/cc0-videos/flower.mp4"
              poster={asset.image}
              controls
              playsInline
              aria-label={asset.name}
            />
          ) : asset.kind === 'Audio' ? (
            <div className="asset-audio">
              <SafeIcon name="FiMusic" size={36} />
              <audio
                controls
                src="https://interactive-examples.mdn.mozilla.net/media/cc0-audio/t-rex-roar.mp3"
                aria-label={asset.name}
              />
            </div>
          ) : asset.kind === 'Document' ? (
            <div className="doc-preview">
              <SafeIcon name="FiFileText" size={40} />
              <strong>{asset.name}</strong>
              <span className="muted">{translate('Document · {n} pages', { n: 2 })}</span>
            </div>
          ) : asset.image ? (
            <button
              className="zoom-button"
              onClick={() => setZoom(!zoom)}
              aria-label={translate(zoom ? 'Zoom out' : 'Zoom in')}
            >
              <img src={asset.image} alt={asset.name} />
            </button>
          ) : (
            <SafeIcon name="FiImage" size={40} />
          )}
          {asset.preparingUntil && (
            <span className="status ready preparing-badge">
              <SafeIcon name="FiLoader" size={11} />
              {translate('Preparing media')}
            </span>
          )}
        </div>

        <label htmlFor="asset-name">{translate('File name')}</label>
        <div className="brand-word-add">
          <input
            id="asset-name"
            className="text-field"
            value={name}
            onChange={(event) => setName(event.target.value)}
            {...readOnlyProps}
          />
          <button
            className="button small"
            disabled={readOnly || !name.trim() || name === asset.name}
            onClick={() => {
              save({ name: name.trim() });
              notify(translate('Renamed'));
            }}
          >
            {translate('Rename')}
          </button>
        </div>

        <h3>{translate('Tags')}</h3>
        <div className="brand-word-list">
          {(asset.tags ?? []).map((item) => (
            <span key={item} className="brand-word use">
              #{item}
              {!readOnly && (
                <button
                  aria-label={translate('Remove {word}', { word: item })}
                  onClick={() =>
                    save({ tags: (asset.tags ?? []).filter((existingTag) => existingTag !== item) })
                  }
                >
                  <SafeIcon name="FiX" size={12} />
                </button>
              )}
            </span>
          ))}
        </div>
        {!readOnly && (
          <form
            className="brand-word-add"
            onSubmit={(event) => {
              event.preventDefault();
              const normalizedTag = newTag.trim().toLowerCase().replace(/^#/, '');
              if (normalizedTag && !(asset.tags ?? []).includes(normalizedTag))
                save({ tags: [...(asset.tags ?? []), normalizedTag] });
              setNewTag('');
            }}
          >
            <input
              className="text-field"
              value={newTag}
              onChange={(event) => setNewTag(event.target.value)}
              placeholder={translate('Add a tag')}
              aria-label={translate('Add a tag')}
            />
            <button className="button small" type="submit">
              <SafeIcon name="FiPlus" size={14} />
              {translate('Add')}
            </button>
          </form>
        )}

        <dl className="asset-facts">
          <div>
            <dt>{translate('Source')}</dt>
            <dd>
              {asset.source === 'Drive' || asset.badges.includes('Linked')
                ? translate('Linked from Drive')
                : translate('Uploaded')}
            </dd>
          </div>
          <div>
            <dt>{translate('Status')}</dt>
            <dd>
              {asset.preparingUntil ? translate('Preparing media') : translate('Ready for posts')}
            </dd>
          </div>
          <div>
            <dt>{translate('Good for')}</dt>
            <dd>{suitable.map((item) => translate(item)).join(', ')}</dd>
          </div>
        </dl>

        <h3>{translate('Used in posts')}</h3>
        {used.length === 0 ? (
          <p className="muted">{translate('Not used in any post yet.')}</p>
        ) : (
          used.map((post) => (
            <button
              key={post.id}
              className="checklist-item list-button"
              onClick={() => {
                onClose();
                navigate({ workspaceId: route.workspaceId, page: 'post', id: post.id });
              }}
            >
              <SafeIcon name="FiCalendar" />
              <span style={{ flex: 1 }}>
                <strong>{post.title}</strong> · {post.channel}
              </span>
              <span className="muted">{formatNovemberDay(post.day)}</span>
            </button>
          ))
        )}

        <div className="button-row" style={{ marginTop: 18 }}>
          <button
            className={`button ${asset.favourite ? 'primary' : ''}`}
            onClick={() => save({ favourite: !asset.favourite })}
            {...readOnlyProps}
          >
            <SafeIcon name="FiStar" />
            {translate(asset.favourite ? 'Favourite' : 'Add to favourites')}
          </button>
          {asset.archived ? (
            <button
              className="button"
              onClick={() => {
                save({ archived: false });
                notify(translate('Restored to the library'));
              }}
              {...readOnlyProps}
            >
              <SafeIcon name="FiRotateCcw" />
              {translate('Restore')}
            </button>
          ) : (
            !confirmArchive && (
              <button
                className="button danger-outline"
                onClick={() => setConfirmArchive(true)}
                {...readOnlyProps}
              >
                <SafeIcon name="FiArchive" />
                {translate('Archive')}
              </button>
            )
          )}
        </div>
        {confirmArchive && (
          <div className="notice-banner status-panel-inline warn" style={{ marginTop: 12 }}>
            <SafeIcon name="FiAlertTriangle" />
            <span>
              {used.length
                ? translate(
                    'It is used in {n} posts. Those posts keep their copy; AMY just stops suggesting it.',
                    { n: used.length }
                  )
                : translate('AMY stops suggesting it. You can restore it from “Archived only”.')}
            </span>
            <button
              className="button small danger"
              onClick={() => {
                save({ archived: true });
                onClose();
                notify(translate('Archived'));
              }}
            >
              {translate('Archive')}
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
